// client/src/features/products/productsApiSlice.js
import { createEntityAdapter } from "@reduxjs/toolkit";
import { apiSlice } from "../../app/api/apiSlice";

const productsAdapter = createEntityAdapter({
  selectId: (product) => product._id || product.id || product.slug,
  sortComparer: (a, b) => {
    const aSort = a.sortOrder ?? 0;
    const bSort = b.sortOrder ?? 0;

    if (aSort !== bSort) return aSort - bSort;

    return (a.name || "").localeCompare(b.name || "");
  },
});

const initialState = productsAdapter.getInitialState({
  total: 0,
  page: 1,
  limit: 100,
  pages: 1,
});

const normalizeProductsResponse = (responseData) => {
  // Supports old raw-array response and new paginated Mongo response:
  // old: [product, product]
  // new: { products: [], total, page, limit, pages }
  const products = Array.isArray(responseData)
    ? responseData
    : responseData?.products || [];

  const normalized = productsAdapter.setAll(initialState, products);

  return {
    ...normalized,
    products,
    total: responseData?.total ?? products.length,
    page: responseData?.page ?? 1,
    limit: responseData?.limit ?? products.length,
    pages: responseData?.pages ?? 1,
  };
};

export const productsApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getProducts: builder.query({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();

        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            searchParams.append(key, value);
          }
        });

        const queryString = searchParams.toString();

        return {
          url: queryString ? `/products?${queryString}` : '/products',
          method: 'GET',
        };
      },
      transformResponse: normalizeProductsResponse,
      providesTags: (result) => {
        if (result?.ids) {
          return [
            { type: 'Product', id: 'LIST' },
            ...result.ids.map((id) => ({ type: 'Product', id })),
          ];
        }

        return [{ type: 'Product', id: 'LIST' }];
      },
    }),

    getProductById: builder.query({
      query: (id) => ({
        url: `/products/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) => [
        { type: 'Product', id: result?._id || result?.id || arg },
      ],
    }),

    uploadProductImage: builder.mutation({
      query: (formData) => ({
        url: '/products/uploads/image',
        method: 'POST',
        body: formData,
      }),
    }),

    createProduct: builder.mutation({
      query: (productData) => ({
        url: '/products',
        method: 'POST',
        body: productData,
      }),
      invalidatesTags: [{ type: 'Product', id: 'LIST' }],
    }),

    updateProduct: builder.mutation({
      query: ({ id, ...productData }) => ({
        url: `/products/${id}`,
        method: 'PATCH',
        body: productData,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'Product', id: arg.id },
        { type: 'Product', id: 'LIST' },
      ],
    }),

    archiveProduct: builder.mutation({
      query: (id) => ({
        url: `/products/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'Product', id: arg },
        { type: 'Product', id: 'LIST' },
      ],
    }),

    hardDeleteProduct: builder.mutation({
      query: (id) => ({
        url: `/products/${id}/hard-delete`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Product', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetProductsQuery,
  useGetProductByIdQuery,
  useUploadProductImageMutation,
  useCreateProductMutation,
  useUpdateProductMutation,
  useArchiveProductMutation,
  useHardDeleteProductMutation,
} = productsApiSlice;